import {
  Grid,
  Typography,
  Divider,
  Tooltip,
  Box,
  IconButton,
} from "@mui/material";
import React, { Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import DownloadIcon from "@mui/icons-material/Download";
import Labels from "../labels/Labels";

import { DataGrid, GridToolbar } from "@mui/x-data-grid";

async function fetchGeneratedFiles(payrollDeductionId) {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/pad/deductions/` + payrollDeductionId + `/files`
  );
  return response;
}

async function downloadGeneratedFile(payrollDeductionId, fileName) {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL +
      `/pad/deductions/` +
      payrollDeductionId +
      `/files/download`,
    {
      params: { fileName },
      responseType: "blob",
    }
  );

  const blob = new Blob([response.data], {
    type: response.headers["content-type"] || "application/octet-stream",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function PayrollDeductionDetails(props) {
  const { payrollDeductionInfo } = props;

  const columns = [
    {
      field: "id",
      headerName: Labels.getLabel("payrollDeductionDetails.column.id", "ID"),
      width: 90,
      hidden: true,
    },
    {
      field: "idMember",
      headerName: Labels.getLabel(
        "payrollDeductionDetails.column.idMember",
        "Member ID"
      ),
      width: 190,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.idMember}
          </Typography>
        );
      },
    },
    {
      field: "firstName",
      headerName: Labels.getLabel(
        "payrollDeductionDetails.column.firstName",
        "First name"
      ),
      width: 120,
    },
    {
      field: "lastName",
      headerName: Labels.getLabel(
        "payrollDeductionDetails.column.lastName",
        "Last name"
      ),
      width: 120,
    },
    {
      field: "amount",
      headerName: Labels.getLabel(
        "payrollDeductionDetails.column.amount",
        "Amount"
      ),
      width: 90,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 14, mb: 0 }} gutterBottom>
            {params.row.amount}
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: Labels.getLabel(
        "payrollDeductionDetails.column.status",
        "Status"
      ),
      width: 150,
      editable: false,
      renderCell: (params) => {
        return (
          <Typography
            sx={{ fontSize: 12, mb: 0, fontWeight: "bold" }}
            gutterBottom
          >
            {params.row.status}
          </Typography>
        );
      },
    },
    {
      field: "creationDate",
      headerName: Labels.getLabel(
        "payrollDeductionDetails.column.creationDate",
        "Creation Date"
      ),
      width: 150,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.creationDate}
          </Typography>
        );
      },
    },
    {
      field: "periodStartDate",
      headerName: Labels.getLabel(
        "payrollDeductionDetails.column.periodStartDate",
        "Period Start Date"
      ),
      width: 150,
      editable: false,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.periodStartDate}
          </Typography>
        );
      },
    },
    {
      field: "periodEndDate",
      headerName: Labels.getLabel(
        "payrollDeductionDetails.column.periodEndDate",
        "Period End Date"
      ),
      width: 150,
      editable: false,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.periodEndDate}
          </Typography>
        );
      },
    },
  ];

  const fileColumns = [
    {
      field: "fileName",
      headerName: "File",
      flex: 1,
      renderCell: (params) => (
        <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
          {params.row.fileName}
        </Typography>
      ),
    },
    {
      field: "artifactType",
      headerName: "Type",
      width: 130,
    },
    {
      field: "groupKey",
      headerName: "Group",
      width: 140,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 110,
      renderCell: (params) => (
        <Tooltip title="Download file">
          <IconButton
            onClick={() =>
              downloadGeneratedFile(
                payrollDeductionInfo.id,
                params.row.fileName
              )
            }
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  async function fetchOnboardingInfoDetail() {
    if (payrollDeductionInfo.id === undefined) return null;
    var url = window.API_URL + `/pad/deductions/` + payrollDeductionInfo.id;
    const response = await HttpService.getAxiosClient().get(url);
    return response;
  }

  const { data, isError, isLoading } = useQuery(
    ["preauthorizedFileDetail", payrollDeductionInfo.id],
    fetchOnboardingInfoDetail
  );

  const {
    data: generatedFilesData,
    isError: generatedFilesError,
    isLoading: generatedFilesLoading,
  } = useQuery(
    ["padDeductionFiles", payrollDeductionInfo.id],
    () => fetchGeneratedFiles(payrollDeductionInfo.id),
    {
      enabled: !!payrollDeductionInfo?.id,
    }
  );

  return (
    <Fragment>
      {isLoading ? (
        <h4>
          {Labels.getLabel("payrollDeductionDetails.Loading", "Loading...")}
        </h4>
      ) : !isError ? (
        <Fragment>
          <Grid
            style={{ marginLeft: "20px", marginRight: "10px", marginTop: 0 }}
            container
            spacing={1.5}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={1}>
                <Typography fontWeight={"bold"}>
                  {Labels.getLabel(
                    "payrollDeductionDetails.Company",
                    "Company:"
                  )}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography>{payrollDeductionInfo.description}</Typography>
              </Grid>
              <Grid item xs={1}>
                <Typography fontWeight={"bold"}>
                  {Labels.getLabel("payrollDeductionDetails.Status", "Status:")}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography>
                  {payrollDeductionInfo && payrollDeductionInfo.status}
                </Typography>
              </Grid>
              <Grid item xs={1.5}>
                <Typography fontWeight={"bold"}>
                  {Labels.getLabel(
                    "payrollDeductionDetails.TotalAmount",
                    "Total Amount:"
                  )}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography>
                  {payrollDeductionInfo && payrollDeductionInfo.amount}
                </Typography>
              </Grid>
              <Grid item xs={1}>
                <Typography fontWeight={"bold"}>
                  {Labels.getLabel(
                    "payrollDeductionDetails.PayPeriod",
                    "Pay Period:"
                  )}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>
                  {payrollDeductionInfo && payrollDeductionInfo.periodStartDate}{" "}
                  - {payrollDeductionInfo && payrollDeductionInfo.periodEndDate}
                </Typography>
              </Grid>
            </Grid>
            <br />
            <br />
            <Divider style={{ margin: "15px" }} />
          </Grid>
          <Box sx={{ px: 2, pb: 2 }}>
            <Typography variant="h7" sx={{ mb: 1 }}>
              Generated Files
            </Typography>
            {generatedFilesLoading ? (
              <Typography>Loading files...</Typography>
            ) : generatedFilesError ? (
              <Typography>Unable to load files.</Typography>
            ) : (
              <div style={{ height: 210 }}>
                <DataGrid
                  rows={(generatedFilesData?.data || []).map((row, index) => ({
                    id: `${row.fileName}-${index}`,
                    ...row,
                  }))}
                  columns={fileColumns}
                  getRowHeight={() => 25}
                  density="compact"
                  pageSize={5}
                  rowsPerPageOptions={[5]}
                  components={{ Toolbar: GridToolbar }}
                  componentsProps={{
                    toolbar: {
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                  disableSelectionOnClick
                  disableColumnFilter
                  disableColumnSelector
                  disableDensitySelector
                />
              </div>
            )}
          </Box>
          <Divider style={{ margin: "15px" }} />
          <div style={{ height: 400 }}>
            <DataGrid
              columnVisibilityModel={{ id: false }}
              disableSelectionOnClick
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              rows={data?.data}
              columns={columns}
              getRowHeight={() => 25}
              density="compact"
              pageSize={10}
              rowsPerPageOptions={[10]}
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </div>
        </Fragment>
      ) : (
        <h4>
          {Labels.getLabel(
            "payrollDeductionDetails.Error",
            "An error ocurred, please try again later..."
          )}
        </h4>
      )}
    </Fragment>
  );
}
